import * as RadixRadioGroup from '@radix-ui/react-radio-group';
import React, { useId } from 'react';
import { notNil } from '../../core/helpers/is-nil';
import { FormHelperText } from '../form-helper-text/form-helper-text';
import { FormInlineNotification, InlineNotification } from '../form-inline-notification/form-inline-notification';
import { FormLabel } from '../form-label/form-label';
import { Radio } from '../radio/radio';
import { RadioIndicator } from '../radio/radio-indicator/radio-indicator';
import styles from './radio-group.module.scss';

export type RadioOption = { value: string; label: string; disabled?: boolean };

type Props = {
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  defaultValue?: string;
  disabled?: boolean;
  label: string;
  mandatory?: boolean;
  inlineNotification?: InlineNotification;
  helperMsg?: string;
};

export const RadioGroup: React.FC<Props> = ({
  options,
  value,
  onChange,
  defaultValue,
  disabled: fullDisabled,
  label,
  mandatory,
  inlineNotification,
  helperMsg,
}) => {
  const radioGroupId = useId();
  const helperId = useId();

  const hasHelper = notNil(helperMsg);
  const hasInlineNotification = notNil(inlineNotification);
  const hasError = notNil(inlineNotification) && inlineNotification.type === 'error';

  return (
    <div className={styles.root}>
      <FormLabel disabled={fullDisabled} htmlFor={radioGroupId} mandatory={mandatory}>
        {label}
      </FormLabel>
      <div className={styles.innerContainer}>
        <RadixRadioGroup.Root
          aria-describedby={hasHelper ? helperId : undefined}
          aria-invalid={hasError}
          aria-label={label}
          className={styles.radioGroup}
          defaultValue={defaultValue}
          id={radioGroupId}
          value={value}
          onValueChange={onChange}
        >
          {options.map(({ value, label, disabled }) => (
            <RadixRadioGroup.Item key={`${label}_${value}`} asChild disabled={fullDisabled ?? disabled} value={value}>
              <Radio
                indicator={
                  <RadixRadioGroup.Indicator>
                    <RadioIndicator />
                  </RadixRadioGroup.Indicator>
                }
              >
                {label}
              </Radio>
            </RadixRadioGroup.Item>
          ))}
        </RadixRadioGroup.Root>
        {hasInlineNotification ? (
          <FormInlineNotification type={inlineNotification.type}>{inlineNotification.message}</FormInlineNotification>
        ) : null}
        {hasHelper ? (
          <FormHelperText disabled={fullDisabled} id={helperId}>
            {helperMsg}
          </FormHelperText>
        ) : null}
      </div>
    </div>
  );
};
